import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

import { useI18n } from '../i18n/I18nProvider';
import { Icon } from './Icon';
import './sheet.css';

/**
 * 从底部滑上来的面板。移动端上比居中弹窗好用 —— 拇指够得着。
 *
 * 用原生 `<dialog>`：焦点陷阱、Esc 关闭、惰性化背景内容都由浏览器做，
 * 自己实现这些很容易漏（尤其是屏幕阅读器那部分）。
 */
export function Sheet(
  { open, title, onClose, children }:
  { open: boolean; title: string; onClose(): void; children: ReactNode },
) {
  const { t } = useI18n();
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog ref={ref} className="sheet"
            onClose={onClose}
            onClick={(e) => {
              // 点遮罩关闭：事件目标是 dialog 本身才算，点面板内部不算
              if (e.target === ref.current) onClose();
            }}>
      <div className="sheet__panel">
        <header className="sheet__head">
          <h2 className="h2">{title}</h2>
          <button type="button" className="sheet__x" onClick={onClose} aria-label={t('action.close')}>
            <Icon name="close" />
          </button>
        </header>
        <div className="sheet__body">{children}</div>
      </div>
    </dialog>
  );
}
